import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, MapPin, Search, Check } from "lucide-react";

/**
 * T6 · Location picker — loads from /api/locations (already tenant-scoped server-side).
 * Emits a shape that matches Locations master: { id, name, type, address, phone, email, contact_person, company_name }.
 * Free-text remains via a passthrough onClear if the caller allows manual override.
 */
export default function LocationPicker({ value, onChange, testid = "location-picker", label = "Location", typeFilter }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/locations");
      setItems(data || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const filtered = items.filter((l) => {
    if (typeFilter && l.type !== typeFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (l.name || "").toLowerCase().includes(s) ||
           (l.company_name || "").toLowerCase().includes(s) ||
           (l.address || "").toLowerCase().includes(s) ||
           (l.contact_person || "").toLowerCase().includes(s);
  });

  return (
    <div>
      <div className="overline">{label}</div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${testid}-trigger`}
            className="mt-1 w-full flex items-center justify-between border border-input bg-background px-3 py-2 text-sm hover:border-primary rounded-md"
          >
            <span className={value ? "" : "text-muted-foreground"}>
              {value ? (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={12} className="text-primary" />
                  <span className="font-semibold">{value.name}</span>
                  {value.address && <span className="text-muted-foreground text-xs"> · {value.address.slice(0, 40)}</span>}
                </span>
              ) : "Select from location master…"}
            </span>
            <ChevronDown size={14} className="text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="rounded-md p-0 w-[380px]">
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search size={14} className="text-muted-foreground" />
            <Input
              data-testid={`${testid}-search`}
              placeholder="Search by name, address, contact…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-md border-0 focus-visible:ring-0 h-8 text-sm"
            />
          </div>
          <div className="max-h-72 overflow-y-auto" data-testid={`${testid}-list`}>
            {filtered.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No locations. Add one under Settings → Locations.</div>}
            {filtered.map((l) => {
              const selected = value?.id === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`${testid}-item-${l.id}`}
                  onClick={() => { onChange(l); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-primary/5 border-b border-border/50 flex items-start justify-between"
                >
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      <MapPin size={11} className="text-primary" />
                      {l.name}
                      <span className="text-[9px] uppercase tracking-widest border border-border px-1 rounded font-normal">{l.type}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {l.address}
                      {l.phone && <> · <span className="mono">{l.phone}</span></>}
                    </div>
                  </div>
                  {selected && <Check size={14} className="text-primary mt-1" />}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
